import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getFamiliesWithStats, createFamily } from '../services/adminService';
import { useAuth } from '../context/AuthContext';
import { Plus, Search } from 'lucide-react';
import './FamiliesScreen.css';

export default function FamiliesScreen() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [familyName, setFamilyName] = useState('');

  const { data: families, isLoading } = useQuery({
    queryKey: ['families'],
    queryFn: getFamiliesWithStats,
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => createFamily(name, currentUser!.uid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['families'] });
      setShowCreateModal(false);
      setFamilyName('');
    },
  });

  const filteredFamilies = families?.filter(family =>
    family.name.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const handleCreate = () => {
    if (familyName.trim()) {
      createMutation.mutate(familyName.trim());
    }
  };

  return (
    <div className="families-screen">
      <div className="screen-header">
        <div>
          <h1>Families</h1>
          <p>Manage all families in your system</p>
        </div>
        <button className="primary-button" onClick={() => setShowCreateModal(true)}>
          <Plus size={20} />
          <span>Create Family</span>
        </button>
      </div>

      <div className="search-bar">
        <Search size={20} />
        <input
          type="text"
          placeholder="Search families..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div className="loading-state">Loading families...</div>
      ) : filteredFamilies.length === 0 ? (
        <div className="empty-state">
          <p>No families found.</p>
        </div>
      ) : (
        <div className="families-grid">
          {filteredFamilies.map((family) => (
            <div
              key={family.id}
              className="family-card"
              onClick={() => navigate(`/families/${family.id}`)}
            >
              <div className="family-header">
                <h3>{family.name}</h3>
                <span className={`status-badge ${family.isActive ? 'active' : 'inactive'}`}>
                  {family.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="family-stats">
                <div className="stat">
                  <span className="stat-value">{family.userCount}</span>
                  <span className="stat-label">Users</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{family.caregiverCount}</span>
                  <span className="stat-label">Caregivers</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{family.memberCount}</span>
                  <span className="stat-label">Members</span>
                </div>
              </div>
              {family.subscriptionTier && (
                <div className="subscription-info">
                  <span className={`subscription-badge ${family.subscriptionTier}`}>
                    {family.subscriptionTier}
                  </span>
                  {family.subscriptionStatus && (
                    <span className="subscription-status">{family.subscriptionStatus}</span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Create New Family</h2>
            <div className="form-group">
              <label>Family Name</label>
              <input
                type="text"
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
                placeholder="Enter family name"
                autoFocus
              />
            </div>
            <div className="modal-actions">
              <button className="secondary-button" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button
                className="primary-button"
                onClick={handleCreate}
                disabled={!familyName.trim() || createMutation.isPending}
              >
                {createMutation.isPending ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

